import ScheduleType from "../pages/Schedules/ScheduleType";

export const MAX_SCHEDULE_NOTE_LENGTH = 500;

export type ScheduleSlotWithNote = {
  id: number;
  consultant_id: number;
  date: string;
  status: number;
  note?: string | null;
};

export type ScheduleLockPayloadItem = {
  id: number | null;
  schedule: string;
};

function parseScheduleCoord(coord: string) {
  const [datePart, timePart, consultantId] = coord.split("|");
  return {
    cellDate: datePart,
    cellTime: timePart.substring(0, 5),
    cellConsultant: parseInt(consultantId, 10),
  };
}

function normalizeScheduleDateParts(date: string) {
  const [agDateStr, agTimeStr] = date.replace("T", " ").split(" ");
  return {
    agDateStr,
    agTime: agTimeStr ? agTimeStr.substring(0, 5) : "",
  };
}

export function findScheduleByCoord(
  coord: string,
  schedules: ScheduleSlotWithNote[],
  status?: number
): ScheduleSlotWithNote | undefined {
  const { cellDate, cellTime, cellConsultant } = parseScheduleCoord(coord);

  return schedules.find((scheduleItem) => {
    const { agDateStr, agTime } = normalizeScheduleDateParts(scheduleItem.date);

    const matchesStatus =
      status === undefined || scheduleItem.status === status;

    return (
      agDateStr === cellDate &&
      agTime === cellTime &&
      scheduleItem.consultant_id === cellConsultant &&
      matchesStatus
    );
  });
}

export function scheduleSlotHasLockNote(
  scheduleItem?: ScheduleSlotWithNote | null
): boolean {
  if (!scheduleItem || scheduleItem.status !== ScheduleType.LOCKED) {
    return false;
  }

  return Boolean(scheduleItem.note?.trim());
}

export function findLockedScheduleNote(
  coord: string,
  schedules: ScheduleSlotWithNote[]
): string | null {
  const lockedSchedule = findScheduleByCoord(
    coord,
    schedules,
    ScheduleType.LOCKED
  );

  const note = lockedSchedule?.note?.trim();
  return note ? note : null;
}

export function buildSchedulesToLockPayload(
  dateSelected: string[],
  schedules: ScheduleSlotWithNote[]
): ScheduleLockPayloadItem[] {
  return dateSelected.map((coord) => {
    const unlockedSchedule = findScheduleByCoord(
      coord,
      schedules,
      ScheduleType.UNLOCKED
    );

    return {
      id: unlockedSchedule?.id ?? null,
      schedule: coord,
    };
  });
}

export function truncateScheduleNote(note: string): string {
  return note.trim().slice(0, MAX_SCHEDULE_NOTE_LENGTH);
}

export function resolveAppointmentScheduleNote(appointment: {
  note?: string | null;
  schedule?: { note?: string | null };
}): string {
  const fromSchedule = appointment.schedule?.note?.trim();
  if (fromSchedule) {
    return fromSchedule;
  }

  return appointment.note?.trim() ?? "";
}
